import React from "react";
import Dimension from "models/Dimension";
import MapWrapper from "components/map/MapWrapper"
import AnalyticsChart from 'components/analytics/AnalyticsChart'
import AnalyticsService from "services/AnalyticsService";
import AnchorService from "services/AnchorService";
import Anchor from "models/Anchor"

function subtractDays(date: Date, daysPast: number) {
    let tmp =  new Date(date)
    tmp.setDate(tmp.getDate() - daysPast)
    tmp.setHours(0, 0, 0, 0)
    return tmp
}

function num2str(n: number, precision: number) {
    const stringWithThreeDecimals = n.toFixed(precision);
    return parseFloat(stringWithThreeDecimals).toString();
}

function daysDiff(d1: Date, d2: Date) {
    //d2.setHours(0, 0, 0, 0)
    const differenceInTime = d2.getTime() - d1.getTime()
    return Math.round(differenceInTime / (1000 * 3600 * 24))
}

function makeMonthPeriod() {
    let begin = new Date()
    begin.setMonth(begin.getMonth() - 1)
    begin.setHours(0, 0, 0, 0)

    let end =  new Date()
    end.setDate(end.getDate() - 1)
    return [begin, end]
}

const EditDimensionMonitor: React.FC<{dimension: Dimension}> = (props) => {
    const [mapData, setMapData] = React.useState<any>(null)
    const [chartData, setChartData] = React.useState<any>(null)

    React.useEffect(() => {
        initialize()
    }, []);

    async function initialize() {

        const [dbData, anchors] = await Promise.all([
            AnalyticsService.getDimensionData({dimension: props.dimension.id}),
            AnchorService.getAnchors({dimension: props.dimension.id})
        ])
        // Anchor[]

        setMapData(prepareMapData(dbData, anchors))
    }

    function prepareMapData(dbData: any, anchors: Anchor[]) {
        if (!dbData || !dbData.locations || !anchors) {
            return null
        }

        let locationsMap: { [key: string]: any } = {}
        for (const loc of props.dimension.locations) {
            if (!loc.enabled) {
                continue
            }
            locationsMap[loc.id] = {
                id: loc.id,
                name: loc.name
            }
        }

        let anchorsMap: any = {}
        for (const anchor of anchors) {
            let locItem = locationsMap[anchor.location]
            if (!locItem) {
                continue;
            }

            anchorsMap[anchor.id] = anchor

            if (!anchor.geolocation || (anchor.geolocation.coordinates[0] == 0 && anchor.geolocation.coordinates[1] == 0)) {
                continue
            }

            if (!locItem.gps) {
                locItem.gps = { lat: 0, lng: 0 }
                locItem.num_gps = 0
            }

            locItem.gps.lat += anchor.geolocation.coordinates[1]
            locItem.gps.lng += anchor.geolocation.coordinates[0]
            locItem.num_gps += 1
        }

        for (const locItem of Object.values(locationsMap)) {
            if (locItem.gps) {
                locItem.gps.lat = locItem.gps.lat / locItem.num_gps
                locItem.gps.lng = locItem.gps.lng / locItem.num_gps
                delete locItem.num_gps
            }
        }

        let maxSessions = 1
        for (const dbItem of dbData.locations) {
            if (dbItem.num_sessions > maxSessions) {
                maxSessions = dbItem.num_sessions
            }
        }

        let resultLocations = []
        for (const dbItem of dbData.locations) {
            let locItem = locationsMap[dbItem.location]
            if (!locItem || !locItem.gps) {
                continue;
            }

            let daysNotVisited = daysDiff(dbItem.last_visited, new Date())
            let notVisitedVal = Math.min(daysNotVisited / 14, 1)

            let anchorDeviationVal = dbItem.avg_anchor_deviation ? Math.min(dbItem.avg_anchor_deviation / 0.5, 1) : undefined
            let anchorDeviationColor = anchorDeviationVal !== undefined
                ? `hsl(${120 * (1 - anchorDeviationVal)}, 100%, 50%)`
                : undefined;
            locItem.params = {
                days_not_visited: {
                    title: 'Not visited',
                    value: - Math.max(notVisitedVal, 0.05),
                    valueText: `${daysNotVisited} d`,
                    color: `hsl(${120 *(1 - notVisitedVal)}, 100%, 50%)`
                },
                num_sessions: {
                    title: 'Sessions',
                    value: dbItem.num_sessions / maxSessions,
                    valueText: `${dbItem.num_sessions}`,
                    color: `hsl(${120 /** dbItem.num_sessions / maxSessions*/}, 100%, 50%)`
                },
                num_localized: {
                    title: 'Localized',
                    value: dbItem.num_localized / maxSessions,
                    valueText: `${dbItem.num_localized}`,
                    color: `hsl(${120 * dbItem.num_localized / dbItem.num_sessions}, 100%, 50%)`
                },
                avg_pct_anchors_localized: {
                    title: 'Avg % anchors localized',
                    value: dbItem.avg_pct_anchors_localized,
                    valueText: `${Math.round(dbItem.avg_pct_anchors_localized * 100)}%`,
                    color: `hsl(${120 * dbItem.avg_pct_anchors_localized}, 100%, 50%)`
                },
                avg_anchor_deviation: {
                    title: 'Avg anchor deviation',
                    value: anchorDeviationVal,
                    valueText: dbItem.avg_anchor_deviation ? num2str(dbItem.avg_anchor_deviation, 3) : undefined,
                    color: anchorDeviationColor
                }
            }
            resultLocations.push(locItem)
        }

        let resultAnchors: any[] = []
        for (const dbAnchor of dbData.anchors) {
            const anchor = anchorsMap[dbAnchor.anchor_id]
            if (!anchor || !anchor.geolocation || (anchor.geolocation.coordinates[0] == 0 && anchor.geolocation.coordinates[1] == 0)) {
                continue
            }
            
            let location = locationsMap[dbAnchor.location]

            let daysNotLocalized = dbAnchor.last_localized ? daysDiff(dbAnchor.last_localized, new Date()) : undefined
            let notLocalizedVal = daysNotLocalized !== undefined && daysNotLocalized >= 0 ? Math.min(daysNotLocalized / 14, 1) : 1

            let anchorDeviationVal = dbAnchor.avg_deviation ? Math.min(dbAnchor.avg_deviation / 0.5, 1) : 0

            let anchorItem = {
                location_name: location ? location.name : dbAnchor.location,
                anchor_id: dbAnchor.anchor_id,
                //name: anchor.name,
                name: dbAnchor.anchor_id,
                date_localized: dbAnchor.last_localized,
                avg_deviation: dbAnchor.avg_deviation,
                gps: {
                    lat: anchor.geolocation.coordinates[1],
                    lng: anchor.geolocation.coordinates[0]
                },
                params: {
                    days_not_localized: {
                        title: 'Not Localized',
                        value: - Math.max(notLocalizedVal, 0.05),
                        valueText: `${daysNotLocalized !== undefined && daysNotLocalized >= 0 ? daysNotLocalized : '-'} d`,
                        color: `hsl(${120 *(1 - notLocalizedVal)}, 100%, 50%)`
                    },
                    avg_deviation: {
                        title: 'Avg deviation',
                        value: anchorDeviationVal,
                        valueText: num2str(dbAnchor.avg_deviation ? dbAnchor.avg_deviation : 0, 3),
                        color: dbAnchor.avg_deviation
                               ? `hsl(${120 * (1 - anchorDeviationVal)}, 100%, 50%)`
                               : `hsl(120, 0%, 50%)`
                    }
                }
            }
            resultAnchors.push(anchorItem)
        }

        return {
            locations: resultLocations,
            anchors: resultAnchors
        }
    }

    const onMarkerClickCallback = React.useCallback(async (item: any) => {
        console.log(`onMarkerClickCallback: ${item.id}`)
        let period = makeMonthPeriod()
        if (item.anchor_id) {
            let dbData = await AnalyticsService.getAnchorData({
                anchor: item.anchor_id,
                period: period
            })

            const chart: any = {
                period: period,
                title: `Anchor: ${item.anchor_id}`,
                items: dbData.map((item: any) => {
                    return {
                        date: item.date,
                        avg_deviation: item.avg_deviation,
                        location_localized: item.location_localized,
                        anchor_localized: item.anchor_localized,
                        num_first_anchor: item.num_first_anchor
                    }
                })
            }
            setChartData(chart)
        } else {
            if (!item.id) {
                return
            }
            let dbData = await AnalyticsService.getLocationData({
                dimension: props.dimension.id,
                location: item.id,
                period: period
            })

            const chart: any = {
                period: period,
                title: `Location: ${item.name}`,
                items: dbData.map((item: any) => {
                    return {
                        date: item.date,
                        num_sessions: item.num_sessions,
                        num_localized: item.num_localized,
                        avg_pct_anchors_localized: item.avg_pct_anchors_localized,
                        avg_anchor_deviation: item.avg_anchor_deviation,
                        avg_duration: item.num_sessions > 0 ? item.sum_duration / item.num_sessions : 0
                    }
                })
            }
            setChartData(chart)
        }
    }, []);

    const monitorConfig = React.useMemo(() => ({
        locations: mapData ? mapData.locations : [],
        anchors: mapData ? mapData.anchors : [],
        onMarkerClick: onMarkerClickCallback
    }), [mapData, onMarkerClickCallback]);

    return (
        <div style={{display: "flex", flexGrow:1, flexDirection: "column"}}>
            <div style={{height: "600px"}}>
                <MapWrapper
                    //onClickHandler={onMapClick}
                    geolocation = {{
                        liveGeolocation: true,
                    }}
                    monitor = {monitorConfig}
                />
            </div>
            <AnalyticsChart height={200} fillGaps={false} data={{
                config: {
                    dateGroup: 'Daily',
                    period: chartData ? chartData.period : [],
                    title: chartData ? chartData.title : undefined,
                },
                data: chartData ? chartData.items : []
            }}/>
        </div>
    );
};

export default EditDimensionMonitor;
